import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { Alert, Badge, Button, Card, CardBody, CardFooter, CardHeader, Col, Form, Input, Media, Row, Spinner } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faCalendarAlt,
  faCalendarCheck,
  faCalendarTimes,
  faCheckCircle,
  faClipboardCheck,
  faFileInvoiceDollar,
  faMapMarkerAlt,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons'
import Loader from '../../common/Loader'
import Avatar from '../../common/Avatar'
import LightBoxGallery from '../../common/LightBoxGallery'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage'
import { Toast } from '../../UI/toast/Toast'
import { getServiceRequest, serviceRequestAction } from '../../../helpers/api/marketplace'
import { formatMoney, providerCardData, storageUrl } from '../marketplaceConstants'
import { REQUEST_STATUS, formatVisit } from './requestConstants'
import ConversationMessage from './ConversationMessage'
import ProposeVisitModal from './ProposeVisitModal'
import QuoteModal from './QuoteModal'

const firstErrors = (errors) =>
  Object.entries(errors || {}).reduce((acc, [field, messages]) => ({ ...acc, [field]: [].concat(messages)[0] }), {})

const confirmDialog = (options) =>
  Swal.fire({
    type: 'question',
    showCancelButton: true,
    confirmButtonColor: '#9b27c7',
    cancelButtonColor: '#bbb',
    cancelButtonText: 'Volver',
    ...options
  })

const Counterpart = ({ side, request }) => {
  if (side === 'provider') {
    return (
      <div>
        <h6 className="mb-0">{request.owner?.name || 'Propietario'}</h6>
        <p className="fs--1 text-600 mb-0">{request.property?.property ? `Inmueble ${request.property.property}` : ''}</p>
      </div>
    )
  }

  const card = providerCardData(request.provider)
  return (
    <Media className="align-items-center">
      {card && <Avatar src={card.photoUrl} name={card.name} size="l" rounded={card.isCompany ? 'soft' : 'circle'} className="mr-2" />}
      <Media body>
        <h6 className="mb-0">{card?.name}</h6>
        <p className="fs--1 text-600 mb-0">Proveedor</p>
      </Media>
    </Media>
  )
}

const RequestSummary = ({ side, request, quotes, can, busy, actions }) => {
  const status = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending
  const accepted = quotes.find((quote) => quote.status === 'accepted')
  const photos = (request.photos || []).map((photo) => storageUrl(photo.path))

  return (
    <Card className="mb-3">
      <CardBody>
        <Badge color={`soft-${status.color}`} pill className="mb-2">
          {status.label}
        </Badge>
        <h5 className="mb-3">
          {side === 'owner' ? (
            <Link to={`/dashboard/marketplace/services/${request.service?.id}`} className="text-900">
              {request.service?.title}
            </Link>
          ) : (
            request.service?.title
          )}
        </h5>

        {request.visit_date ? (
          <p className="fs--1 text-700 mb-2">
            <FontAwesomeIcon icon={faCalendarCheck} className="text-success mr-2" />
            Visita confirmada: {formatVisit(request.visit_date, request.visit_time)}
          </p>
        ) : (
          <p className="fs--1 text-700 mb-2">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-500 mr-2" />
            Fecha sugerida: {formatVisit(request.suggested_date, request.suggested_time)}
          </p>
        )}
        <p className="fs--1 text-700 mb-3">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="text-500 mr-2" />
          {request.address}
        </p>

        {accepted && (
          <Alert color="success" className="fs--1 py-2">
            Contratado por <strong>{formatMoney(accepted.amount)}</strong>.
          </Alert>
        )}

        {photos.length > 0 && (
          <LightBoxGallery images={photos}>
            {(openImage) => (
              <div className="d-flex flex-wrap mb-3" style={{ gap: 6 }}>
                {photos.map((photo, index) => (
                  <button key={photo} type="button" className="p-0 border rounded overflow-hidden" style={{ width: 56, height: 56 }} onClick={() => openImage(index)}>
                    <img src={photo} alt="" className="w-100 h-100" style={{ objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            )}
          </LightBoxGallery>
        )}

        {side === 'provider' && (
          <div className="d-flex flex-column" style={{ gap: 8 }}>
            {can.confirm_visit && (
              <Button color="primary" size="sm" disabled={busy} onClick={actions.confirmVisit}>
                <FontAwesomeIcon icon={faCalendarCheck} className="mr-1" />
                Confirmar visita
              </Button>
            )}
            {can.propose_visit && (
              <Button color="falcon-default" size="sm" disabled={busy} onClick={actions.proposeVisit}>
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                Proponer otra fecha
              </Button>
            )}
            {can.cancel_visit && (
              <Button color="falcon-default" size="sm" className="text-danger" disabled={busy} onClick={actions.cancelVisit}>
                <FontAwesomeIcon icon={faCalendarTimes} className="mr-1" />
                Cancelar visita
              </Button>
            )}
            {can.mark_visited && (
              <Button color="falcon-default" size="sm" disabled={busy} onClick={actions.markVisited}>
                <FontAwesomeIcon icon={faClipboardCheck} className="mr-1" />
                Visita realizada
              </Button>
            )}
            {can.send_quote && (
              <Button color="primary" size="sm" disabled={busy} onClick={actions.sendQuote}>
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-1" />
                Enviar cotización
              </Button>
            )}
            {can.complete && (
              <Button color="success" size="sm" disabled={busy} onClick={actions.complete}>
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                Marcar como completado
              </Button>
            )}
          </div>
        )}

        {can.cancel && (
          <Button color="link" size="sm" className="text-danger px-0 mt-2" disabled={busy} onClick={actions.cancel}>
            {side === 'provider' ? 'Rechazar solicitud' : 'Cancelar solicitud'}
          </Button>
        )}
      </CardBody>
    </Card>
  )
}

const ServiceRequestConversation = ({ side, listPath }) => {
  const { id } = useParams()
  const conversation = useSWRFetch(['marketplace-request', id], (key, value) => getServiceRequest(value), { refreshInterval: 5000 })
  const [busy, setBusy] = useState(false)
  const [text, setText] = useState('')
  const [modal, setModal] = useState(null)
  const [modalKey, setModalKey] = useState(0)
  const scrollRef = useRef()

  const data = conversation.data?.data
  const messages = useMemo(() => data?.messages || [], [data])
  const quotes = useMemo(() => data?.quotes || [], [data])
  const lastId = messages.length ? messages[messages.length - 1].id : null

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [lastId])

  const run = async (action, payload = {}, { success, onErrors } = {}) => {
    setBusy(true)
    try {
      const response = await serviceRequestAction(id, action, payload)
      conversation.mutate(response, false)
      if (success) Toast.fire({ type: 'success', title: typeof success === 'function' ? success(response) : success })
      return response
    } catch (error) {
      const errors = error?.data?.errors
      if (errors && onErrors) {
        onErrors(firstErrors(errors))
      } else if (errors) {
        Toast.fire({ type: 'error', title: Object.values(firstErrors(errors))[0] })
      } else {
        handleErrorMessage(error?.data || error)
      }
      return null
    } finally {
      setBusy(false)
    }
  }

  const openModal = (name) => {
    setModalKey((key) => key + 1)
    setModal(name)
  }

  const sendText = async (event) => {
    event.preventDefault()
    const body = text.trim()
    if (!body || busy) return
    const response = await run('messages', { body })
    if (response) setText('')
  }

  if (conversation.error) {
    return (
      <Alert color="warning">
        No encontramos esta solicitud.
        <Link to={listPath} className="ml-1">
          Volver a las solicitudes
        </Link>
      </Alert>
    )
  }

  if (conversation.isLoading || !data) {
    return (
      <Card>
        <Loader padding="py-5" />
      </Card>
    )
  }

  const { request, can } = data
  const status = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending
  const quoteById = quotes.reduce((acc, quote) => ({ ...acc, [quote.id]: quote }), {})

  const actions = {
    confirmVisit: () =>
      confirmDialog({
        title: '¿Confirmar la visita?',
        text: `Quedará agendada para el ${formatVisit(request.suggested_date, request.suggested_time)}, la fecha que sugirió el propietario.`,
        confirmButtonText: 'Sí, confirmar'
      }).then((result) => result.value && run('visit/confirm', {}, { success: 'Visita confirmada.' })),
    proposeVisit: () => openModal('propose'),
    markVisited: () =>
      confirmDialog({
        title: '¿Marcar la visita como realizada?',
        text: 'Después podrás enviar la cotización con lo que viste.',
        confirmButtonText: 'Sí, ya fui'
      }).then((result) => result.value && run('visit/done', {}, { success: 'Visita marcada como realizada.' })),
    sendQuote: () => openModal('quote'),
    complete: () =>
      confirmDialog({
        title: '¿Marcar el servicio como completado?',
        text: 'La solicitud se cierra y ya no se podrán enviar mensajes.',
        confirmButtonText: 'Sí, completado'
      }).then((result) => result.value && run('complete', {}, { success: 'Servicio completado.' })),
    cancelVisit: () =>
      confirmDialog({
        title: '¿Cancelar la visita?',
        text: `El propietario verá que cancelaste la visita del ${formatVisit(request.visit_date, request.visit_time)}. Después podrás confirmar o proponer otra fecha.`,
        type: 'warning',
        input: 'textarea',
        inputPlaceholder: 'Motivo (opcional)',
        confirmButtonColor: '#e63757',
        confirmButtonText: 'Sí, cancelar visita'
      }).then((result) => !result.dismiss && run('visit/cancel', { reason: result.value || null }, { success: 'Visita cancelada.' })),
    cancel: () => {
      const isProvider = side === 'provider'
      return confirmDialog({
        title: isProvider ? '¿Rechazar la solicitud?' : '¿Cancelar la solicitud?',
        text: isProvider
          ? 'El propietario verá que la rechazaste y la conversación se cierra.'
          : 'El proveedor verá que la cancelaste y la conversación se cierra.',
        type: 'warning',
        input: 'textarea',
        inputPlaceholder: 'Motivo (opcional)',
        confirmButtonColor: '#e63757',
        confirmButtonText: isProvider ? 'Sí, rechazar' : 'Sí, cancelar'
      }).then(
        (result) =>
          !result.dismiss &&
          run('cancel', { reason: result.value || null }, { success: isProvider ? 'Solicitud rechazada.' : 'Solicitud cancelada.' })
      )
    }
  }

  const acceptQuote = (quote) =>
    confirmDialog({
      title: `¿Aceptar el presupuesto de ${formatMoney(quote.amount)}?`,
      text: 'El servicio quedará contratado y ya no se podrán recibir otras cotizaciones en esta solicitud.',
      confirmButtonText: 'Sí, contratar'
    }).then(
      (result) =>
        result.value &&
        run(`quotes/${quote.id}/accept`, {}, {
          success: (response) =>
            response.data.rewards?.xp
              ? `¡Servicio contratado! Ganaste ${response.data.rewards.xp.toLocaleString('es-VE')} XP en Homelan Rewards.`
              : '¡Servicio contratado!'
        })
    )

  const rejectQuote = (quote) =>
    confirmDialog({
      title: '¿Rechazar la cotización?',
      text: 'El proveedor podrá enviarte una nueva.',
      type: 'warning',
      input: 'textarea',
      inputPlaceholder: 'Motivo (opcional)',
      confirmButtonText: 'Sí, rechazar'
    }).then((result) => !result.dismiss && run(`quotes/${quote.id}/reject`, { reason: result.value || null }, { success: 'Cotización rechazada.' }))

  const acceptProposal = (message) => run('visit/accept', { message_id: message.id }, { success: 'Visita confirmada.' })

  return (
    <>
      <Button tag={Link} to={listPath} color="link" size="sm" className="px-0 mb-2">
        <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
        {side === 'provider' ? 'Volver a las solicitudes' : 'Volver a mis solicitudes'}
      </Button>

      <Row>
        <Col lg={8} className="order-2 order-lg-1">
          <Card className="mb-3">
            <CardHeader className="bg-light d-flex align-items-center">
              <div className="mr-auto">
                <Counterpart side={side} request={request} />
              </div>
              <Badge color={`soft-${status.color}`} pill>
                {status.label}
              </Badge>
            </CardHeader>
            <div ref={scrollRef} className="p-3" style={{ height: '60vh', minHeight: 320, overflowY: 'auto' }}>
              {messages.map((message) => (
                <ConversationMessage
                  key={message.id}
                  message={message}
                  side={side}
                  quote={quoteById[message.data?.quote_id]}
                  can={can}
                  busy={busy}
                  onAcceptQuote={acceptQuote}
                  onRejectQuote={rejectQuote}
                  onAcceptProposal={acceptProposal}
                />
              ))}
            </div>
            <CardFooter className="bg-light">
              {can.message ? (
                <Form onSubmit={sendText} className="d-flex align-items-end" style={{ gap: 8 }}>
                  <Input
                    type="textarea"
                    rows={2}
                    maxLength={2000}
                    value={text}
                    onChange={({ target }) => setText(target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) sendText(event)
                    }}
                    placeholder="Escribe un mensaje…"
                    style={{ resize: 'none' }}
                  />
                  <Button type="submit" color="primary" disabled={busy || !text.trim()} title="Enviar">
                    {busy ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faPaperPlane} />}
                  </Button>
                </Form>
              ) : (
                <p className="fs--1 text-600 mb-0 text-center">Esta solicitud está cerrada; ya no se pueden enviar mensajes.</p>
              )}
            </CardFooter>
          </Card>
        </Col>
        <Col lg={4} className="order-1 order-lg-2">
          <RequestSummary side={side} request={request} quotes={quotes} can={can} busy={busy} actions={actions} />
        </Col>
      </Row>

      {side === 'provider' && (
        <>
          <ProposeVisitModal
            key={`propose-${modalKey}`}
            isOpen={modal === 'propose'}
            toggle={() => setModal(null)}
            suggested={{ date: request.suggested_date, time: request.suggested_time }}
            onSubmit={async (form, setErrors) => {
              const response = await run('visit/propose', form, { success: 'Propuesta enviada.', onErrors: setErrors })
              if (response) setModal(null)
            }}
          />
          <QuoteModal
            key={`quote-${modalKey}`}
            isOpen={modal === 'quote'}
            toggle={() => setModal(null)}
            onSubmit={async (form, setErrors) => {
              const response = await run('quotes', form, { success: 'Cotización enviada.', onErrors: setErrors })
              if (response) setModal(null)
            }}
          />
        </>
      )}
    </>
  )
}

export default ServiceRequestConversation
